import type { TPlayer } from 'video.js'
import type { XhrResponse } from '|>/shared/types/errors'

/**
 * Returns true if the URL points to an HLS (.m3u8, .m3u) or DASH (.mpd) manifest.
 */
export function isHlsOrDashUrl(url: string): boolean {
  const u = url.toLowerCase()
  return u.includes('.m3u8') || u.includes('.m3u') || u.includes('.mpd')
}

/**
 * Returns the measured throughput of the player.
 */
export function getMeasuredThroughput(player: TPlayer): number | undefined {
  try {
    const vhs = player.tech_?.vhs
    if (!vhs) {
      return undefined
    }
    return vhs.systemBandwidth
  } catch (ignore) {}
}

/**
 * Returns the number of bytes in the response body or the Content-Length header
 */
export function getResponseBytes(response: XhrResponse): number {
  const body = response?.body as unknown

  // try to get the byte length from the response body if available
  if (body != null) {
    if (typeof body === 'string') {
      return new TextEncoder().encode(body).byteLength
    } else if (
      body instanceof ArrayBuffer ||
      ArrayBuffer.isView(body) ||
      Object.prototype.toString.call(body) === '[object ArrayBuffer]' ||
      (typeof body === 'object' &&
        'byteLength' in body &&
        typeof body.byteLength === 'number')
    ) {
      return (body as ArrayBuffer).byteLength
    }
  }

  // fallback to Content-Length header if body is not available or doesn't have byte length
  if (response?.headers != null) {
    const headers = new Headers(response.headers)
    const contentLength = headers.get('Content-Length')
    if (contentLength) {
      const parsed = Number.parseInt(contentLength, 10)
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed
      }
    }
  }

  // if neither body nor Content-Length header is available, return 0 as a safe default
  return 0
}
